/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type {
  ItemActionDetail,
  ItemFocusDetail,
  TagCloudItemData,
  TagCloudProps
} from './tagCloud.type';
import { TagCloudItem, TagItemProps } from './TagCloudItem';
import {
  getAriaProps,
  getItemProps,
  getLayout,
  isEqualItem,
  getKeyUpHandler
} from './tagCloudUtils';
import { getTagCloudNavUtils } from './tagCloudNavUtils';
import { useVisEvent } from '#hooks/PRIVATE_useVisEvents/useVisEvents';
import { useTagCloudDatatip } from './useTagCloudDatatip';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useMemo, useRef } from 'preact/hooks';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { useUser } from '#hooks/UNSAFE_useUser';
import { baseStyles } from './themes/TagCloudStyles.css';
import { useSelection } from '#hooks/PRIVATE_useVisSelection';
import { getItemInfo } from './tagCloudUtils';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { useTagCloudContextMenu } from './useTagCloudContextMenu';
import { ItemInfo } from './tagCloud.type';
import { useVisTouchResponse } from '#hooks/PRIVATE_useVisTouchResponse';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';

type TagCloudWithDimensionsProps<K, D> = Omit<TagCloudProps<K, D>, 'width' | 'height'> & {
  width: number;
  height: number;
};

export function TagCloudWithDimensions<K extends string | number, D extends TagCloudItemData<K>>({
  selectionMode = 'none',
  layout = 'cloud',
  width,
  height,
  items,
  onItemAction,
  onItemHover,
  onItemFocus,
  testId,
  contextMenuConfig,
  datatip,
  ...props
}: TagCloudWithDimensionsProps<K, D>) {
  const testIdProps = useTestId(testId);
  const rootRef = useRef<HTMLDivElement>(null);
  const focusedItemRef = useRef<TagItemProps<K> | null>(null);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const hasData = items.length > 0;
  const supportsSelection = !!(selectionMode && selectionMode !== 'none');
  const supportsAction = onItemAction != null;
  const isReadOnly = supportsAction && onItemHover === null && onItemFocus === null;
  const idToDataMap = new Map(items.map((obj) => [obj.id, obj]));

  const { getDetailFromInfo, getPrevItemInfo, getNextItemInfo } = getTagCloudNavUtils<K, D>(items);

  const { touchResponse, touchResponseStyle } = useVisTouchResponse({
    type: 'auto',
    supportsTouchDragGestures: false,
    rootRef
  });

  const { focusedItemInfo, hoveredItemInfo, onContextMenuDismissed, activeId, eventsProps } =
    useVisEvent<ItemInfo, ItemActionDetail<K, D>, ItemFocusDetail<K, D>, D>(
      touchResponse,
      !isReadOnly && hasData,
      () => !isReadOnly,
      getKeyUpHandler,
      getDetailFromInfo,
      getPrevItemInfo,
      getNextItemInfo,
      onItemAction,
      onItemHover,
      onItemFocus,
      isEqualItem,
      getItemInfo,
      { idx: 0 }
    );

  const {
    datatipContent,
    datatipProps: { 'aria-describedby': datatipAriaDescribedby, ...datatipProps }
  } = useTagCloudDatatip({
    touchResponse,
    datatip,
    rootRef,
    focusedItemInfo,
    focusedItemRef,
    hoveredItemInfo,
    items
  });

  const ariaProps = getAriaProps(
    translations,
    hasData,
    props.accessibleLabel,
    merge([props['aria-describedby'], datatipAriaDescribedby]),
    props['aria-labelledby']
  );

  const idExtracter = (event: Event) => {
    // return focused item for keyboard event.
    // eventsProps should have already updated the focused item
    const info =
      event.type === 'keyup' ? focusedItemInfo : getItemInfo(event.target as HTMLElement);
    return getDetailFromInfo(info).id;
  };

  const { selectionContent, selectionProps } = useSelection<K, D>({
    idExtracter,
    selection: props.selectedIds,
    selectionMode: selectionMode,
    onChange: props.onSelectionChange,
    getDataById: (id) => idToDataMap.get(id)
  });

  const { direction } = useUser();

  const { textMeasureContent, getTextDimensions } = useTextDimensions();
  const layoutTags = useMemo(() => {
    return getTextDimensions && hasData
      ? getLayout(layout, items, width, height, direction === 'rtl', getTextDimensions)
      : [];
  }, [layout, items, hasData, width, height, direction, getTextDimensions]);

  const { contextMenuContent, contextMenuProps } = useTagCloudContextMenu<D>({
    getItemInfo,
    focusedItemInfo,
    rootRef,
    contextMenuConfig: hasData ? contextMenuConfig : undefined,
    items,
    onContextMenuDismissed
  });

  const mergedProps = mergeProps(eventsProps, datatipProps, selectionProps, contextMenuProps);
  return (
    <div
      ref={rootRef}
      tabIndex={isReadOnly ? undefined : 0}
      style={touchResponseStyle}
      class={baseStyles}
      {...testIdProps}
      {...ariaProps}
      {...mergedProps}
      aria-activedescendant={activeId}>
      {layoutTags.map((item, itemIndex) => {
        const itemProps = getItemProps(
          item,
          itemIndex,
          supportsSelection,
          isReadOnly,
          hoveredItemInfo,
          props.selectedIds,
          focusedItemInfo,
          props.highlightedIds,
          activeId
        );
        const isFocusedItem = focusedItemInfo.idx === itemIndex;
        isFocusedItem ? (focusedItemRef.current = item) : null;
        return (
          <TagCloudItem
            fontSize={item.fontSize}
            x={item.x}
            y={item.y}
            accessibleLabel={item.accessibleLabel}
            supportsSelection={supportsSelection}
            supportsAction={supportsAction}
            translations={translations}
            {...itemProps}
          />
        );
      })}
      {textMeasureContent}
      {datatipContent}
      {contextMenuContent}
      {selectionContent}
    </div>
  );
}
